import { useState } from "react";
import { IconButton, Tooltip, Menu, MenuItem, Checkbox, ListItemText, Button, Box, Divider } from "@mui/material";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";

// Small popover multi-select that turns a checked set of deliverables into one
// newline-free, "•"-joined string — meant to sit as a WhatsApp template variable field's
// endAdornment (see WhatsAppBulkSendModal.jsx), not to persist anything itself. WhatsApp
// template parameters can only ever be a single-line string (Meta rejects one containing
// a newline/tab — see sanitizeParam in pluginbackendnode/helpers/whatsappHelper.js), so
// "•" keeps any number of picked deliverables on one line regardless of count.
//
// Deliberately separate from InfluencerTable.jsx's DeliverableColumnsMenu — that one
// manages a list's active columns with create/delete/apply-to-server semantics that don't
// apply here; this one only ever hands the caller a string via onInsert.
function DeliverablePickerButton({ deliverables, onInsert }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [checked, setChecked] = useState(new Set());

  if (!deliverables?.length) return null;

  const handleOpen = (e) => {
    setChecked(new Set());
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleToggle = (name) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleInsert = () => {
    if (!checked.size) return;
    onInsert([...checked].join(" • "));
    handleClose();
  };

  return (
    <>
      <Tooltip title="Insert deliverables">
        <IconButton size="small" edge="end" onClick={handleOpen} sx={{ color: "#9CA3AF", "&:hover": { color: "#FF5200" } }}>
          <PlaylistAddCheckIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {deliverables.map((name) => (
          <MenuItem key={name} onClick={() => handleToggle(name)} dense>
            <Checkbox checked={checked.has(name)} size="small" sx={{ p: 0.5, mr: 1 }} />
            <ListItemText primary={name} primaryTypographyProps={{ fontSize: 13.5 }} />
          </MenuItem>
        ))}
        <Divider />
        <Box sx={{ p: 1, display: "flex", justifyContent: "flex-end" }}>
          <Button size="small" onClick={handleInsert} disabled={!checked.size} sx={{ textTransform: "none" }}>
            Insert{checked.size > 0 ? ` (${checked.size})` : ""}
          </Button>
        </Box>
      </Menu>
    </>
  );
}

export default DeliverablePickerButton;
